<template>
  <p
    class="body-3-bold tool-desc md:text-center sm:text-left sm:text-base pb-4"
  >
    {{ $t("calculator-suite.lotsizeTooltip") }}
  </p>
  <div class="calculator-params-results">
    <section class="params-card">
      <h2 class="body-3-bold">{{ $t("calculator-suite.setParams") }}</h2>
      <div class="inputs">
        <InputSearch
          id="lot-size-options"
          v-model="market"
          name="lot-size-dropdown"
          :label="$t('calculator-suite.market')"
          :placeholder="$t('calculator-suite.tabs.placeholder')"
          :options="
            options.map((i) => ({
              text: $t(i.text),
              value: i.value,
            }))
          "
        />

        <div v-for="input in filteredParams" :key="`${market}-${input.key}`">
          <TVInputNumber
            v-if="!input.options"
            v-model="input.value"
            :label="$t(input.text)"
            :placeholder="$t(input.placeholder)"
            :name="input.text"
            :tooltip-message="$t(input.tooltip)"
            :tooltip-max-width="246"
          />
          <InputSearch
            v-else
            :id="`select-${input.key}`"
            v-model="input.value"
            :name="input.text"
            :label="$t(input.text)"
            :placeholder="$t(input.placeholder)"
            :model-value="input.value"
            :options="input.options"
          />
        </div>
      </div>
      <button
        class="primary-button-medium"
        @click="calculate"
        :disabled="!isFormValid"
      >
        {{ $t("calculator-suite.calculate") }}
      </button>
    </section>

    <section class="results-card w-full flex flex-column">
      <ResultsSkeleton v-if="isLoading" class="p-3" />
      <div
        v-else
        class="result-info mb-4"
        style="background-color: transparent"
      >
        <h2 class="body-3-bold">{{ $t("calculator-suite.results") }}:</h2>
        <div
          v-for="(result, index) in results"
          :key="index"
          class="item p-3"
          style="background-color: #ecf1f7"
        >
          <h4 class="body-4">{{ $t(result.text) }}:</h4>
          <span class="body-4-bold" v-if="result.value !== ''">
            {{ result.value }}
            {{ result.currency }}
          </span>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, computed, watch, defineEmits } from "vue";
import { useI18n } from "vue-i18n";
import TVInputNumber from "../InputNumber.vue";
import InputSearch from "../InputSearch.vue";
import ResultsSkeleton from "./ResultsSkeleton.vue";
import {
  options,
  leverageMT45XLEV,
  optionsCurrencyForex,
  typeContract,
} from "./utils/dropdownOptions";
import { lotsizeCalculate } from "./utils/calculateRequest";

const { t } = useI18n();
const market = ref("");
const isLoading = ref(false);

watch(
  () => market.value,
  (value) => {
    const currentParams = market.value.includes("stock") ? paramsStock.value : paramsForex.value;
    currentParams.forEach((param) => {
      param.value = null;
    });
    results.value.forEach((result) => {
      result.value = "";
    });
  }
);

const filteredParams = computed(() => {
  if (!market.value) return [];
  return market.value.includes("stock") ? paramsStock.value : paramsForex.value;
});

const isFormValid = computed(() => {
  return (
    market.value &&
    filteredParams.value.every(
      (param) =>
        param.value !== null && param.value !== "" && param.value !== undefined
    )
  );
});

const calculate = async () => {
  isLoading.value = true;

  const currentParams = market.value.includes("stock") ? paramsStock.value : paramsForex.value;
  const paramsData = currentParams.reduce((acc, { key, value }) => {
    acc[key] = value;
    return acc;
  }, {});

  const apiResponse = await lotsizeCalculate(market.value, paramsData);

  Object.entries(apiResponse).forEach(([key, value]) => {
    const resultItem = results.value.find((r) => r.key === key);
    if (resultItem) {
      resultItem.value = value;
      if (key === "money_at_risk") {
        resultItem.currency = apiResponse.currency_used ?? "USD";
      }
    }
  });

  isLoading.value = false;
};

const paramsForex = ref([
  {
    text: "calculator-suite.accountCurrency",
    placeholder: "calculator-suite.accountCurrencyPlaceholder",
    options: optionsCurrencyForex,
    key: "account_currency",
    value: null,
  },
  {
    text: "calculator-suite.accountbalance",
    placeholder: "calculator-suite.accountbalancePlaceholder",
    tooltip: "calculator-suite.accountbalanceTooltip",
    key: "account_balance",
    value: null,
  },
  {
    text: "calculator-suite.riskPerc",
    placeholder: "calculator-suite.riskPercPlaceholder",
    tooltip: "calculator-suite.riskPercTooltip",
    key: "risk_percentage",
    value: null,
  },
  {
    text: "calculator-suite.leverage",
    placeholder: "calculator-suite.selectAnOption",
    options: leverageMT45XLEV.map((i) => ({
      text: i.name,
      value: i.code,
    })),
    key: "leverage",
    value: null,
  },
  {
    text: "calculator-suite.typeContract",
    placeholder: "calculator-suite.selectAnOption",
    options: typeContract,
    key: "type_of_contract",
    value: null,
  },
  {
    text: "calculator-suite.stopLoss",
    placeholder: "calculator-suite.stopLossPlaceholder",
    tooltip: "calculator-suite.stopLossTooltip",
    key: "stop_loss_pips",
    value: null,
  },
]);

const paramsStock = ref([
  {
    text: "calculator-suite.stockPrice",
    placeholder: "calculator-suite.stockPricePlc",
    key: "stock_price",
    tooltip: "",
    value: null,
  },
  {
    text: "calculator-suite.accountbalance",
    placeholder: "calculator-suite.accountbalancePlaceholder",
    tooltip: "calculator-suite.accountbalanceTooltip",
    key: "account_balance",
    value: null,
  },
  {
    text: "calculator-suite.leverage",
    placeholder: "calculator-suite.selectAnOption",
    options: leverageMT45XLEV.map((i) => ({
      text: i.name,
      value: i.code,
    })),
    key: "leverage",
    value: null,
  },
  {
    text: "calculator-suite.typeContract",
    placeholder: "calculator-suite.selectAnOption",
    options: typeContract,
    key: "type_of_contract",
    value: null,
  },
  {
    text: "calculator-suite.riskPerc",
    placeholder: "calculator-suite.riskPercPlaceholder",
    tooltip: "calculator-suite.riskPercTooltip",
    key: "risk_percentage",
    value: null,
  },
  {
    text: "calculator-suite.stopLoss",
    placeholder: "calculator-suite.stopLossPlaceholder",
    tooltip: "calculator-suite.stopLossTooltip",
    key: "stop_loss_pips",
    value: null,
  },
]);

const results = ref([
  {
    text: "calculator-suite.lots",
    value: null,
    key: "lots",
    currency: "lots",
  },
  {
    text: "calculator-suite.units",
    value: null,
    key: "units",
    currency: "units",
  },
  {
    text: "calculator-suite.moneyRisk",
    value: null,
    key: "money_at_risk",
    currency: "USD",
  },
]);
</script>
